import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  Autocomplete,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  CancelOutlined as CancelIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorAccountIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  AccountTree as AccountTreeIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';

const TeamMembersManagement = ({
  users = [],
  teams = [],
  currentUser,
  onEdit,
  onRemove,
  onAssign,
  onAddMember,
  onToggleLeadManager,
  openEditDialog,
  setOpenEditDialog,
  editUser,
  setEditUser,
  userroles = [],
  whatsappSettings = { is_whatsapp_connected: false, whatsapp_messaging_enabled: false }
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    designation: '',
    mobile: '',
    role: 'AG', // Default to Agent
    is_lead_manager: false,
  });
  const [errors, setErrors] = useState({});

  // Check if current user can manage this user/team
  const canManageUser = (userToCheck) => {
    if (!currentUser) return false;
    
    // Admin can manage everyone
    if (currentUser.role === 'AD') return true;
    
    // Managers can only manage their team members
    if (currentUser.role === 'MG') {
      // Check if user is in the same team as the manager
      return userToCheck.team_id === currentUser.team_id;
    }
    
    // Agents can't manage anyone
    return false;
  };

  // Role display helpers
  const getRoleDisplay = (role) => {
    switch (role) {
      case 'AD': return 'Admin';
      case 'MG': return 'Manager';
      case 'AG': return 'Agent';
      default: return role;
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'AD': return <AdminPanelSettingsIcon fontSize="small" />;
      case 'MG': return <SupervisorAccountIcon fontSize="small" />;
      case 'AG': return <PersonIcon fontSize="small" />;
      default: return <PersonIcon fontSize="small" />;
    }
  };

  // Handle table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = !filterRole || user.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  // Dialogs handlers
  const handleOpenAssignDialog = (user) => {
    setSelectedUser(user);
    setSelectedTeam(teams.find(team => team.id === user.team_id) || null);
    setOpenAssignDialog(true);
  };

  const handleAssignTeam = async () => {
    if (selectedUser && selectedTeam) {
      const success = await onAssign(selectedUser.id, selectedTeam.id);
      if (success) {
        setOpenAssignDialog(false);
      }
    }
  };

  const handleAddUser = async () => {
    // Validate form
    const newErrors = {};
    if (!newUser.name) newErrors.name = 'Name is required';
    if (!newUser.email) newErrors.email = 'Email is required';
    if (!newUser.mobile) newErrors.mobile = 'Mobile is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    const success = await onAddMember(newUser);
    if (success) {
      setOpenAddDialog(false);
      setNewUser({
        name: '',
        email: '',
        designation: '',
        mobile: '',
        role: 'AG', // Default to Agent
        is_lead_manager: false,
      });
      setErrors({});
    }
  };

  return (
    <Box>
      {/* Filters and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Search Members"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 250, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={filterRole}
              label="Role"
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="AD">Admin</MenuItem>
              <MenuItem value="MG">Manager</MenuItem>
              <MenuItem value="AG">Agent</MenuItem>
            </Select>
          </FormControl>
          
          
        </Box>
      </Paper>

      {/* Team Members Table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Reports To</TableCell>
                <TableCell align="center">Lead Manager</TableCell>
                <TableCell align="center">Number Masking</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => {
                  const team = teams.find(t => t.id === user.team_id);
                  // Find the manager object by ID from reports_to data
                  const manager = user.reports_to ? users.find(u => u.id === user.reports_to.id) : null;
                  
                  return (
                    <TableRow
                      hover
                      key={user.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ bgcolor: user.role === 'AD' ? 'error.main' : user.role === 'MG' ? 'secondary.main' : 'primary.main', width: 32, height: 32, mr: 1 }}>
                            {getRoleIcon(user.role)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {user.name}
                            </Typography>
                            {user.designation && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {user.designation}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {user.email}
                        </Typography>
                        {user.mobile && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {user.mobile}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleDisplay(user.role)}
                          size="small"
                          color={
                            user.role === 'AD' ? 'error' :
                            user.role === 'MG' ? 'secondary' : 'primary'
                          }
                          icon={getRoleIcon(user.role)}
                        />
                      </TableCell>
                      <TableCell>
                        {user.reports_to ? (
                          <Typography variant="body2">
                            {user.reports_to.name}
                            <Typography variant="caption" color="text.secondary" display="block">
                              {manager ? getRoleDisplay(manager.role) : ''}
                            </Typography>
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={user.is_lead_manager || false}
                          onChange={(e) => onToggleLeadManager(user.id, e.target.checked)}
                          disabled={!canManageUser(user) || user.role === 'AD' || user.role === 'MG'}
                          color="secondary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={user.lead_number_masking_enabled ? 'Enabled' : 'Disabled'}
                          size="small"
                          color={user.lead_number_masking_enabled ? 'success' : 'default'}
                          variant={user.lead_number_masking_enabled ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          {canManageUser(user) && (
                            <>
                              <Tooltip title="Edit User">
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={() => { 
                                    // Ensure mobile_action_required_mandatory is set with default value if missing
                                    const userToEdit = {
                                      ...user,
                                      mobile_action_required_mandatory: user.mobile_action_required_mandatory !== undefined 
                                        ? user.mobile_action_required_mandatory 
                                        : true
                                    };
                                    setEditUser(userToEdit); 
                                    setOpenEditDialog(true); 
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {user.role !== 'AD' && (
                                <Tooltip title="Remove User">
                                  <IconButton size="small" color="error" onClick={() => onRemove(user.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Assign Team Dialog */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="sm">
        <DialogTitle>Assign Team for {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, minWidth: 300 }}>
            <FormControl fullWidth>
              <InputLabel>Select Team</InputLabel>
              <Select
                value={selectedTeam?.id || ''}
                label="Select Team"
                onChange={(e) => {
                  const teamId = e.target.value;
                  const team = teams.find(t => t.id === teamId);
                  setSelectedTeam(team || null);
                }}
              >
                <MenuItem value="">
                  <em>No Team (Unassigned)</em>
                </MenuItem>
                {teams.map(team => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignTeam}>
            Assign Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Full Name"
              fullWidth
              required
              value={editUser?.name || ''}
              onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
            />
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              value={editUser?.email || ''}
              onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              label="Designation"
              fullWidth
              required
              value={editUser?.designation || ''}
              onChange={(e) => setEditUser({ ...editUser, designation: e.target.value })}
            />
            <TextField
              label="Mobile"
              fullWidth
              required
              value={editUser?.mobile || ''}
              onChange={(e) => setEditUser({ ...editUser, mobile: e.target.value })}
              error={!!errors.mobile}
              helperText={errors.mobile}
            />
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                value={editUser?.role || ''}
                label="Role"
                onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
              >
                {Array.isArray(userroles) && userroles.map((role) => (
                  <MenuItem 
                    key={role.value} 
                    value={role.value}
                    disabled={
                      (role.value === 'AD' && users.some(user => user.role === 'AD' && user.id !== editUser?.id))
                    }
                  >
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Mobile App Settings - For AG, MG, and AD roles */}
            {editUser && (editUser.role === 'AG' || editUser.role === 'MG' || editUser.role === 'AD') && (
              <Box sx={{ 
                p: 2, 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 1,
                bgcolor: 'background.paper',
                mt: 1
              }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Mobile App Settings
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editUser.mobile_action_required_mandatory !== false}
                      onChange={(e) => setEditUser({ 
                        ...editUser, 
                        mobile_action_required_mandatory: e.target.checked 
                      })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Action Required Popup - Mandatory
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When enabled, you must complete action on all pending leads before closing the popup. 
                        When disabled, you can dismiss the popup without taking action.
                      </Typography>
                    </Box>
                  }
                  sx={{ display: 'flex', alignItems: 'flex-start', m: 0, mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editUser.lead_number_masking_enabled || false}
                      onChange={(e) => setEditUser({ 
                        ...editUser, 
                        lead_number_masking_enabled: e.target.checked 
                      })}
                      disabled={!whatsappSettings.is_whatsapp_connected || !whatsappSettings.whatsapp_messaging_enabled}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Lead Number Masking
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When enabled, lead phone numbers will be masked for this user in the mobile app. 
                        Only works if WhatsApp is connected and messaging is enabled at account level.
                      </Typography>
                      {(!whatsappSettings.is_whatsapp_connected || !whatsappSettings.whatsapp_messaging_enabled) && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                          {!whatsappSettings.is_whatsapp_connected 
                            ? 'WhatsApp integration is not connected. Please connect WhatsApp in Integrations settings.'
                            : 'WhatsApp messaging is not enabled. Please enable it in WhatsApp Settings.'}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={async () => {
              if (editUser) {
                // Create a copy of editUser without email if it hasn't changed
                const userData = {...editUser};
                
                // Find the original user to compare if email has changed
                const originalUser = users.find(u => u.id === editUser.id);
                
                // If email hasn't changed, remove it from the update data
                if (originalUser && originalUser.email === userData.email) {
                  delete userData.email;
                }
                
                const result = await onEdit(editUser.id, userData);
                if (result === true) {
                  setOpenEditDialog(false);
                  setEditUser(null);
                } else if (typeof result === 'object') {
                  // Backend returned field errors
                  setErrors(result);
                } else {
                  // Generic error, dialog remains open
                  setErrors({ general: 'Failed to update user. Please try again.' });
                }
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Full Name"
              fullWidth
              required
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
            />
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              label="Designation"
              fullWidth
              value={newUser.designation}
              onChange={(e) => setNewUser({ ...newUser, designation: e.target.value })}
            />
            <TextField
              label="Mobile"
              fullWidth
              required
              value={newUser.mobile}
              onChange={(e) => setNewUser({ ...newUser, mobile: e.target.value })}
              error={!!errors.mobile}
              helperText={errors.mobile}
            />
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                label="Role"
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                {Array.isArray(userroles) && userroles.map((role) => (
                  <MenuItem 
                    key={role.value} 
                    value={role.value}
                    disabled={
                      (role.value === 'AD' && users.some(user => user.role === 'AD'))
                    }
                  >
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Assign to Team</InputLabel>
              <Select
                value={newUser.team_id || ''}
                label="Assign to Team"
                onChange={(e) => setNewUser({ ...newUser, team_id: e.target.value })}
              >
                <MenuItem value="">
                  <em>No Team (Unassigned)</em>
                </MenuItem>
                {teams.map(team => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {newUser.role === 'MG' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={newUser.is_lead_manager || false}
                    onChange={(e) => setNewUser({ ...newUser, is_lead_manager: e.target.checked })}
                    name="is_lead_manager"
                    color="secondary"
                  />
                }
                label="Is Lead Manager"
              />
            )}
            {currentUser?.role === 'AD' && (
              <FormControl fullWidth>
                <InputLabel>Reports To</InputLabel>
                <Select
                  value={newUser.reports_to || ''}
                  label="Reports To"
                  onChange={(e) => setNewUser({ ...newUser, reports_to: e.target.value })}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {users
                    .filter(u => ['MG', 'AD'].includes(u.role) && u.id !== newUser?.id)
                    .map(user => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({getRoleDisplay(user.role)})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddUser}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamMembersManagement;
